import { faArrowsRotate, faCheck, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Stack } from '@mantine/core';
import capitalize from 'lodash.capitalize';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, CardV2 as Card, Icon, List, Well } from '@feathr/components';
import { useBlackboxVersion } from '@feathr/extender/hooks';
import { useFlags, useRole } from '@feathr/report_components';

interface IProps {
  handleFactoryReset: () => void;
  isResetting: boolean;
}

function FeathrLiteOnboardingCard({
  handleFactoryReset,
  isResetting,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { blackboxVersion } = useBlackboxVersion();
  const { showOnboarding, enforceOnboarding } = useFlags();
  const { isGlobal } = useRole();

  return (
    <>
      <Card disabled={isResetting}>
        <Card.Header title={t('Feathr Platform - Lite - Onboarding')} />
        <Card.Content action={capitalize(blackboxVersion?.DB_ENV)} title={t('Environment')} />
        <Card.Content addVerticalGap={true} title={t('Flags')}>
          <Well label={t('Show Onboarding')}>
            <Icon
              color={showOnboarding ? 'green' : 'red'}
              icon={showOnboarding ? faCheck : faXmark}
            />
          </Well>
          <Well label={t('Onboarding Enforced')}>
            <Icon
              color={enforceOnboarding ? 'green' : 'red'}
              icon={enforceOnboarding ? faCheck : faXmark}
            />
          </Well>
        </Card.Content>
      </Card>

      <Card disabled={isResetting}>
        <Card.Header
          description={t('Fully purge the account and reset as a clean slate.')}
          isCollapsible={true}
          title={t('Factory Reset')}
        />
        <Card.Content>
          <Stack>
            <Trans t={t}>
              <List hasBullets={true}>
                <List.Item>All milestones and tasks will be reset to "Not started."</List.Item>
                <List.Item>The payment method for the account will be deleted.</List.Item>
                <List.Item>
                  ALL DATA associated with the account will be deleted. Flights, Events, Campaigns,
                  Groups, etc will all be deleted.
                </List.Item>
                <List.Item>
                  Current users (excluding those with global roles) will not have access to the
                  account and must be re-invited.
                </List.Item>
              </List>
            </Trans>
          </Stack>
        </Card.Content>
        <Card.Actions>
          <Button
            disabled={!isGlobal || isResetting}
            onClick={handleFactoryReset}
            prefix={<Icon icon={faArrowsRotate} />}
            tooltip={
              !isGlobal
                ? t('Global role required')
                : isResetting
                  ? t('Reset in progress')
                  : undefined
            }
            type={'danger'}
          >
            {t('Reset')}
          </Button>
        </Card.Actions>
      </Card>
    </>
  );
}

export default observer(FeathrLiteOnboardingCard);
